<template>
  <article v-if="page && Object.keys(page).length" :class="$style.home">
    <header>
      <MediaBannerHandler
        v-if="page.mediaBanner && page.mediaBanner.enabled"
        :media-banner="page.mediaBanner"
        :is-header-banner="true"
      ></MediaBannerHandler>
      <PostFeaturedImage
        v-else-if="page.featuredImage"
        decoding="async"
        fetchpriority="high"
        loading="eager"
        :featured-image="page.featuredImage"
        :ga4-data="
          JSON.stringify({
            click_type: 'hero',
            component: 'PagePrimary',
            content_group: 'page',
            gtm_tag: 'image',
            file_name: `${page.featuredImage.sourceUrl.split('/').pop()}`,
          })
        "
        :srcset="page.imageSrcset"
        :is-wide="page.featuredImageWidth === 'wide'"
      ></PostFeaturedImage>
      <h1 v-if="page.mainTitle">{{ page.mainTitle }}</h1>
    </header>
    <div :class="['container', $style.grid]">
      <div v-if="page.quickLinks">
        <PreparedAnchor
          v-for="(link, key) in page.quickLinks"
          :key="key"
          :class="$style['quick-link']"
          :to="link.link"
          :behavior="link.link_behavior"
          >{{ link.link_text }}</PreparedAnchor
        >
      </div>
      <section v-if="page.featured" :class="$style.exhibitions">
        <h2 v-if="page.featured.header" class="space-0-above" v-html="page.featured.header"></h2>
        <div class="space-48-64-above">
          <PreparedAnchor :to="page.featured.more_link">
            <picture class="aspect-container">
              <img
                :alt="page.featured.exhibition.featuredImage.altText"
                class="aspect-image"
                loading="lazy"
                :src="page.featured.exhibition.featuredImage.sourceUrl"
                :srcset="page.featured.exhibition.featuredImage.srcset"
                sizes="(max-width: 960px) 100vw, 1280px"
              />
            </picture>
          </PreparedAnchor>
          <h3 class="space-12-16-above">
            <PreparedAnchor :to="page.featured.exhibition.url">{{
              page.featured.exhibition.title
            }}</PreparedAnchor>
          </h3>
          <div :class="$style.time" v-html="page.featured.more_link_text"></div>
        </div>
      </section>
      <section v-if="page.exhibitions.items.length" :class="$style.exhibitions">
        <h2 class="space-0-above">On View Now</h2>
        <div
          v-for="(exhibition, key) in page.exhibitions.items"
          :key="key"
          class="space-48-64-above"
        >
          <PreparedAnchor :to="exhibition.url">
            <picture class="aspect-container">
              <img
                :alt="exhibition.featuredImage.altText"
                class="aspect-image"
                loading="lazy"
                :src="exhibition.featuredImage.sourceUrl"
                :srcset="exhibition.imageSrcset"
                sizes="(max-width: 960px) 100vw, 1280px"
              />
            </picture>
          </PreparedAnchor>
          <h3 class="space-12-16-above">
            <PreparedAnchor :to="exhibition.url">{{ exhibition.title }}</PreparedAnchor>
          </h3>
          <time :class="$style.time" v-html="exhibition.dates.formatted"></time>
          <router-link
            v-if="
              exhibition.callToAction &&
              exhibition.callToAction.label &&
              exhibition.callToAction.link
            "
            :class="$style.cta"
            :to="exhibition.callToAction.link"
            >{{ exhibition.callToAction.label }}</router-link
          >
        </div>
        <PreparedAnchor
          v-if="page.exhibitions.more_link"
          class="button-secondary"
          :to="page.exhibitions.more_link"
        >
          {{ page.exhibitions.more_link_text }}
        </PreparedAnchor>
      </section>
      <UpcomingEvents
        :class="[$style.narrow, 'space-48-64-above']"
        :upcoming-events="page.events"
      ></UpcomingEvents>
      <FromTheCollection class="space-48-64-above"></FromTheCollection>
      <section :class="[$style.grid, $style.narrow]">
        <article
          v-for="(block, key) in page.blocks"
          :key="key"
          :class="[style[block.type], $style.lined, 'space-48-64-above']"
        >
          <div v-if="block.type !== 'text_below'" :class="$style.image">
            <PreparedAnchor :to="block.more_link" :behavior="block.more_link_behavior">
              <picture class="aspect-container">
                <img
                  :alt="block.image.altText"
                  class="aspect-image"
                  loading="lazy"
                  :src="block.image.sourceUrl"
                  :srcset="block.image.imageSrcset"
                  sizes="(min-width: 960px) 620px, 100vw"
                />
              </picture>
            </PreparedAnchor>
          </div>
          <div v-if="block.type !== 'text_below'" :class="$style.text">
            <h2 v-if="block.heading" :class="$style.heading">
              <PreparedAnchor :to="block.more_link" :behavior="block.more_link_behavior">
                {{ block.heading }}
              </PreparedAnchor>
            </h2>
            <p v-if="block.text" v-html="block.text"></p>
            <PreparedAnchor
              class="more-link"
              :to="block.more_link"
              :behavior="block.more_link_behavior"
            >
              {{ block.more_link_text }}
            </PreparedAnchor>
          </div>
          <div v-if="block.type === 'text_below'" :class="$style.frame">
            <h2 v-if="block.heading" :class="$style.heading">
              <PreparedAnchor :to="block.more_link" :behavior="block.more_link_behavior">
                {{ block.heading }}
              </PreparedAnchor>
            </h2>
            <PreparedAnchor :to="block.more_link" :behavior="block.more_link_behavior">
              <picture class="aspect-container">
                <img
                  :alt="block.image.altText"
                  class="aspect-image"
                  loading="lazy"
                  :src="block.image.sourceUrl"
                  :srcset="block.image.imageSrcset"
                  sizes="(min-width: 960px) 620px, 100vw"
                />
              </picture>
            </PreparedAnchor>
            <p v-if="block.subheading" :class="$style.subheading">{{ block.subheading }}</p>
            <p v-if="block.text" v-html="block.text"></p>
            <PreparedAnchor
              class="more-link"
              :to="block.more_link"
              :behavior="block.more_link_behavior"
            >
              {{ block.more_link_text }}
            </PreparedAnchor>
          </div>
        </article>
      </section>
    </div>
  </article>
</template>

<script setup lang="ts">
import { BootStrap } from '@/types/global.interface';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { Image } from '@/types/common.interface';
import FromTheCollection from '@/components/common/FromTheCollection.vue';
import gql from 'graphql-tag';
import { homepageQuery } from '@/queries/HomepageQuery';
import { inject, ref, useCssModule } from 'vue';
import MediaBannerHandler from '@/components/mediaBanner/MediaBannerHandler.vue';
import { MediaItem } from '@/types/mediaBanner.interface';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import PreparedAnchor from '@/components/common/PreparedAnchor.vue';
import { storeToRefs } from 'pinia';
import { UpcomingEvent } from '@/types/event.interface';
import UpcomingEvents from '@/components/common/UpcomingEvents.vue';
import { useMainStore } from '@/stores/mainStore.js';
import { useQuery } from 'villus';

const bootstrap = inject<BootStrap>('bootstrap');

const store = useMainStore();
const { useBootstrap } = storeToRefs(store);

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();
const style = useCssModule();

interface Block {
  image: Image;
  heading: string;
  more_link: string;
  more_link_behavior: string;
  more_link_text: string;
  subheading: string;
  text: string;
  type: string;
}

interface Exhibition {
  callToAction?: {
    label: string;
    link: string;
  };
  dates: {
    formatted: string;
  };
  featuredImage: Image;
  imageSrcset: string;
  title: string;
  url: string;
}

interface Page {
  blocks: Array<Block>;
  events: {
    items: Array<UpcomingEvent>;
    more_link: string;
    more_link_text: string;
    title: string;
  };
  excerpt: string;
  exhibitions: {
    items: Array<Exhibition>;
    more_link: string;
    more_link_text: string;
  };
  featured: {
    exhibition: Exhibition;
    header: string;
    more_link: string;
    more_link_text: string;
  };
  featuredImage: Image;
  imageSrcset: string;
  mediaBanner: MediaItem;
  textPrompt: string;
}

const page = ref<Page>();
const shouldSkip = ref(true);

const { data, error } = useQuery({
  onData: (data) => {
    if (data && Object.keys(data).length) {
      page.value = data.homepage;
      setMetaTitle('Home');
      setMetaDescription(data.homepage.excerpt);
      dataLoaded();
    }

    if (data && Object.keys(data).length === 0) {
      handleError('Home', error);
    }
  },
  query: gql`
    ${homepageQuery}
  `,
  skip: shouldSkip,
});

if (useBootstrap.value) {
  shouldSkip.value = true;
  page.value = bootstrap.initial.main.posts.homepage;
  setMetaTitle('Home');
  setMetaDescription(bootstrap.initial.main.posts.homepage.excerpt);
  dataLoaded();
}

if (!useBootstrap.value) {
  shouldSkip.value = false;
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: gql`
            ${homepageQuery}
          `,
          variables: {},
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss">
@use '@/styles/links.module.scss' as *;
@use '@/styles/summary.module.scss' as *;
</style>

<style lang="scss" module>
:global(.aspect-container) {
  background: none;
}

.cta {
  @include subtitle-2;
  @include space-8-10-above;

  & {
    display: inline-block;
  }
}
.exhibitions {
  margin-top: 4rem;

  &::before {
    @include line;

    & {
      margin-top: -2rem;
    }
  }

  :global(.button-secondary) {
    margin-right: 0;
    margin-top: 2rem;
  }
}

.home {
  h1 {
    margin-bottom: 0;
    padding: 0;

    &::after,
    &::before {
      border-bottom: none;
    }
  }
  h2,
  h3 {
    a {
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  a:not(:global(.button-secondary)) {
    &:focus {
      color: $white;

      h2,
      h3 {
        @include focus;
      }
    }
  }
}

.lined {
  &::before {
    @include line;

    & {
      margin-top: -2rem;
    }
  }

  :global(.aspect-container) {
    @include space-0-above;
  }

  :global(.aspect-image) {
    margin: 0 auto;
  }
}

.quick-link {
  @include heading-3;
  @include space-0-above;

  & {
    display: block;
    text-decoration: none;
    width: fit-content;
  }

  &::before {
    content: '\2192\00A0';
  }

  &:first-of-type {
    @include space-24-32-above;
  }

  &:hover {
    text-decoration: underline;
  }
}

.prompt {
  @include space-48-64-above;
}

.subheading {
  @include heading-4;
}

.text {
  margin-top: 1rem;

  h2 + p {
    @include space-8-10-above;
  }
}

.text_below {
  .heading {
    margin: 1rem auto 0 auto;
  }

  :global(.aspect-container) {
    margin-top: 1rem;
  }
}

.text_below,
.text_left,
.text_right {
  @include space-48-64-above;

  h2 {
    margin: 0 auto;
  }
}

.time {
  @include subtitle-2;
  @include space-8-10-above;

  & {
    display: block;
  }
}

@media (min-width: $break-point) {
  .exhibitions {
    grid-column: span 12;
  }

  .grid {
    @include grid;
  }

  .narrow {
    grid-column: span 12;
  }

  .text {
    justify-self: center;
    margin-top: 0;
  }

  .text_below,
  .text_left,
  .text_right {
    grid-column: span 12;
  }

  .text_below {
    grid-column: span 6;

    .heading {
      grid-row: 1;
      margin-top: 0;
    }
  }

  .text_left,
  .text_right {
    display: grid;
    grid-column: span 12;
    grid-column-gap: $column-gutter-large;
    grid-template-areas: 'a b';
    grid-template-columns: 1fr 1fr;

    .image {
      @include space-7-above;
    }
  }

  .text_left {
    .image {
      grid-area: b;
    }

    .text {
      grid-area: a;
    }
  }

  .text_right {
    .image {
      grid-area: a;
    }

    .text {
      grid-area: b;
    }
  }
}
</style>
